import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { generateNews } from '../services/api';
import useFadeIn from '../hooks/useFadeIn';
import '../css/sections.css';

const CAT_COLORS = {
  Leadership: '#c9a84c',
  Business:   '#7eb8c9',
  Women:      '#c97eb8',
  Healthcare: '#7ec9a0',
  Education:  '#c9a07e',
  Technology: '#7e8fc9',
};

/* ── Unsplash random images ───────────────────────────────────────────
   1. Create a free app at https://unsplash.com/developers
   2. Paste your "Access Key" below (or load it from your .env file).
   Without a key the cards simply use the built-in fallback image.      */
const UNSPLASH_ACCESS_KEY = 'PASTE_YOUR_UNSPLASH_ACCESS_KEY_HERE';

const CAT_QUERY = {
  Leadership: 'executive leadership boardroom',
  Business:   'business office corporate',
  Women:      'businesswoman leader',
  Healthcare: 'healthcare hospital doctor',
  Education:  'university education classroom',
  Technology: 'technology startup innovation',
};

const hasKey = () => UNSPLASH_ACCESS_KEY && !UNSPLASH_ACCESS_KEY.startsWith('PASTE_');

/* One request per category per session (10 random photos each) — keeps you
   well inside Unsplash's 50 requests/hour limit for demo apps. */
const POOLS = {};
const getPool = (category) => {
  if (!hasKey()) return Promise.resolve([]);
  if (!POOLS[category]) {
    const q = encodeURIComponent(CAT_QUERY[category] || 'business leadership');
    POOLS[category] = fetch(
      `https://api.unsplash.com/photos/random?query=${q}&count=10&orientation=landscape&content_filter=high&client_id=${UNSPLASH_ACCESS_KEY}`
    )
      .then(r => { if (!r.ok) throw new Error(`Unsplash ${r.status}`); return r.json(); })
      .then(list => (Array.isArray(list) ? list : [list]).map(ph => ({
        src:       `${ph.urls.raw}&w=800&h=450&fit=crop&q=75&auto=format`,
        credit:    ph.user?.name || 'Unsplash',
        creditUrl: `${ph.user?.links?.html || 'https://unsplash.com'}?utm_source=leadership_news&utm_medium=referral`,
      })))
      .catch(err => { console.warn('Unsplash failed:', err.message); delete POOLS[category]; return []; });
  }
  return POOLS[category];
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* Built-in image that ALWAYS works (no network): branded gradient per category.
   Used whenever the API gives no image or a photo URL fails to load. */
const makeFallback = (category = 'News') => {
  const c = CAT_COLORS[category] || '#c9a84c';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 360" preserveAspectRatio="xMidYMid slice">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#14161d"/><stop offset="1" stop-color="#232733"/>
      </linearGradient>
      <radialGradient id="r" cx=".8" cy=".2" r=".7">
        <stop offset="0" stop-color="${c}" stop-opacity=".45"/><stop offset="1" stop-color="${c}" stop-opacity="0"/>
      </radialGradient>
    </defs>
    <rect width="640" height="360" fill="url(#g)"/>
    <rect width="640" height="360" fill="url(#r)"/>
    <g stroke="${c}" stroke-opacity=".12" stroke-width="1">
      ${Array.from({ length: 14 }).map((_, i) => `<line x1="${i * 60 - 200}" y1="360" x2="${i * 60 + 160}" y2="0"/>`).join('')}
    </g>
    <circle cx="520" cy="80" r="90" fill="none" stroke="${c}" stroke-opacity=".25" stroke-width="2"/>
    <circle cx="520" cy="80" r="55" fill="none" stroke="${c}" stroke-opacity=".18" stroke-width="2"/>
    <text x="40" y="320" font-family="Georgia,serif" font-size="150" font-weight="700" fill="${c}" fill-opacity=".14">${category.charAt(0).toUpperCase()}</text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

/* Make any API response safe: fill missing fields so a card never breaks */
const normalizeItem = (item = {}, i = 0) => ({
  title:    item.title    || item.headline || 'Leadership Insights',
  excerpt:  item.excerpt  || item.summary  || item.description || 'Read the latest on business and leadership.',
  category: CAT_COLORS[item.category] ? item.category : 'Business',
  readTime: item.readTime || item.read_time || `${3 + (i % 3)} min read`,
  image:    item.image || item.imageUrl || item.urlToImage || '',
  url:      item.url || '#',
});

/* Static fallback — always shown if API fails.
   Add your own image per story in the `image` field:
     • local file → put it in  public/images/news/  and use '/images/news/name.jpg'
     • or any full URL → 'https://images.unsplash.com/photo-XXXX?w=800&h=450&fit=crop'
   Leave `image` as '' to get a random Unsplash photo / built-in fallback instead. */
const STATIC_NEWS = [
 {
  title: 'XTEND Reality Expansion Ltd. and JFB Construction Holdings Complete Business Combination, Raising $110 Million in Total Capital',
  excerpt:
    'XTEND Reality Expansion Ltd. and JFB Construction Holdings have completed their business combination, raising $110 million in total capital to support the company’s next phase of growth.',
  category: 'Business',
  readTime: '5 min read',
  image:
    'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=1600&auto=format&fit=crop',
  url: '/article/pr1',
},

 {
  title: 'U.S. Markets Face Fresh Pressure as Oil Surges Above $100 and Treasury Yields Rise',
  excerpt:
    'U.S. financial markets are facing renewed pressure as oil prices surge above $100 a barrel while rising Treasury yields add to concerns over inflation and borrowing costs.',
  category: 'Business',
  readTime: '9 min read',
  image:
    'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=1200&auto=format&fit=crop',
  url: '/blog/U.S.%20Markets%20Face%20Fresh%20Pressure%20as%20Oil%20Surges%20Above%20$100%20and%20Treasury%20Yields%20Rise',
},

 {
  title: 'Wall Street Watches Inflation Data as Investors Reassess the Federal Reserve’s Next Move',
  excerpt:
    'Investors on Wall Street are closely watching new inflation data as they reassess expectations for the Federal Reserve’s next interest-rate decision and the outlook for financial markets.',
  category: 'Business',
  readTime: '11 min read',
  image:
    'https://images.unsplash.com/photo-1559526324-593bc073d938?w=1600&h=900&fit=crop&q=90',
  url: '/blog/us5',
},

{
  title: 'Anthropic Reveals New AI Cybersecurity Incidents During Model Testing',
  excerpt:
    'Anthropic has disclosed new cybersecurity incidents identified during AI model testing, highlighting emerging security challenges as increasingly capable AI systems are developed and evaluated.',
  category: 'Technology',
  readTime: '11 min read',
  image:
    'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=1200&auto=format&fit=crop',
  url: '/blog/us4',
},

  // {
  //   title: 'New AI Tools Move Beyond Documentation in Healthcare',
  //   excerpt:
  //     'Healthcare organizations are beginning to explore AI tools that can support clinical workflows rather than simply record conversations and generate notes.',
  //   category: 'Healthcare',
  //   readTime: '3 min read',
  //   image:
  //     'https://images.unsplash.com/photo-1584982751601-97dcc096659c?q=80&w=1200&auto=format&fit=crop',
  //   url: '/news/ai-tools-beyond-documentation-healthcare',
  // },

  // {
  //   title: 'U.S. Technology Companies Face Growing Questions Over AI Safety',
  //   excerpt:
  //     'The rapid expansion of AI is bringing renewed attention to safety measures, independent reviews and how companies manage increasingly capable systems.',
  //   category: 'Technology',
  //   readTime: '4 min read',
  //   image:
  //     'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=1200&auto=format&fit=crop',
  //   url: '/news/us-tech-companies-ai-safety-questions',
  // },
];

/* Whole-card link: <Link> for internal routes, plain <a> for external URLs
   (react-router's <Link> can't navigate to full http(s) URLs). */
const isExternal = (url = '') => /^(https?:)?\/\//i.test(url);

function NewsCardLink({ url, className, children }) {
  const style = { textDecoration: 'none', color: 'inherit' };
  if (isExternal(url)) {
    return (
      <a href={url} className={className} style={style} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return <Link to={url} className={className} style={style}>{children}</Link>;
}

/* Shows the photo; on any error swaps to the built-in fallback image */
function CardImage({ src, alt, category, credit, creditUrl }) {
  const fallback = makeFallback(category);
  const [current, setCurrent] = useState(src || fallback);
  const [loaded, setLoaded]   = useState(false);

  useEffect(() => { setCurrent(src || fallback); setLoaded(false); }, [src]);

  return (
    <>
      <img
        src={current}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={loaded ? 'loaded' : ''}
        onLoad={() => setLoaded(true)}
        onError={() => { if (current !== fallback) setCurrent(fallback); }}
      />
      {credit && current === src && (
        <span
          className="news-card-credit"
          role="link"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(creditUrl, '_blank', 'noopener,noreferrer');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              window.open(creditUrl, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          Photo: {credit} / Unsplash
        </span>
      )}
    </>
  );
}

export default function News() {
  const [news,    setNews]    = useState(() => STATIC_NEWS.map(normalizeItem)); // start visible immediately
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [photos,  setPhotos]  = useState({});   // index -> { src, credit, creditUrl }
  const [shuffleKey, setShuffleKey] = useState(0);
  const ref = useFadeIn(true);

  const loadNews = (topic = 'global business leadership trends 2026') => {
    setLoading(true); setError(''); setShuffleKey(k => k + 1);
    generateNews(topic, 6)
      .then(r => {
        const raw  = r?.data?.data ?? r?.data?.news ?? r?.data?.articles ?? r?.data;
        const list = Array.isArray(raw) ? raw : [];
        if (list.length) setNews(list.map(normalizeItem));
        else setError('No new stories right now. Showing cached stories.');
      })
      .catch(err => {
        console.error('News API failed:', err?.response?.status, err?.message);
        setError('');
        /* keep current news — never blank the section */
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Attempt to load AI news, but section is already populated with static news
    loadNews();
  }, []);

  /* Give every card without its own image a RANDOM Unsplash photo of its category */
  useEffect(() => {
    let cancelled = false;
    const cats = [...new Set(news.filter(n => !n.image).map(n => n.category))];
    Promise.all(cats.map(c => getPool(c).then(pool => [c, shuffle(pool)]))).then(entries => {
      if (cancelled) return;
      const pools = Object.fromEntries(entries);
      const used  = {};
      const next  = {};
      news.forEach((n, i) => {
        const pool = pools[n.category];
        if (n.image || !pool?.length) return;
        used[n.category] = used[n.category] || 0;
        next[i] = pool[used[n.category]++ % pool.length];   // no repeats inside a category
      });
      setPhotos(next);
    });
    return () => { cancelled = true; };
  }, [news, shuffleKey]);

  return (
    <section className="sec sec-dark" id="ai-news" ref={ref}>
      <div className="wrap">
        <div className="sec-head center">
          {/* <div className="news-ai-badge fade-in" aria-label="AI-Powered content">
            <span className="news-ai-dot" aria-hidden="true" />
            AI-Powered News
          </div> */}
          <h2 className="sec-title fade-in">Latest Leadership News</h2>
          <p className="sec-sub fade-in">
            Real-time business and leadership insights — fresh, relevant, and always on point.
          </p>
        </div>

        {error && (
          <p style={{
            textAlign: 'center', fontSize: '.82rem', color: 'var(--white40)',
            marginBottom: 16, padding: '8px 16px',
          }}>
            {error}
          </p>
        )}

        <div className="news-grid">
          {loading
            ? /* Skeleton — 6 placeholder cards */
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="news-card is-skeleton fade-in" aria-hidden="true">
                  <div className="news-card-img news-skeleton" style={{ borderRadius: 0 }} />
                  <div className="news-card-body">
                    <div className="news-skeleton" style={{ height: 14, marginBottom: 4,  borderRadius: 3 }} />
                    <div className="news-skeleton" style={{ height: 14, width: '80%', marginBottom: 10, borderRadius: 3 }} />
                    <div className="news-skeleton" style={{ height: 10, borderRadius: 3 }} />
                    <div className="news-skeleton" style={{ height: 10, width: '60%', marginTop: 2, borderRadius: 3 }} />
                  </div>
                </div>
              ))
            : news.map((item, i) => (
                <NewsCardLink key={i} url={item.url} className="news-card fade-in">
                  <div className="news-card-img">
                    <CardImage
                      src={item.image || photos[i]?.src}
                      alt={item.title}
                      category={item.category}
                      credit={!item.image ? photos[i]?.credit : ''}
                      creditUrl={photos[i]?.creditUrl}
                    />
                    <span
                      className="news-card-cat"
                      style={{ color: CAT_COLORS[item.category] || 'var(--gold)' }}
                    >
                      {item.category}
                    </span>
                  </div>
                  <div className="news-card-body">
                    <h3>{item.title}</h3>
                    <p>{item.excerpt}</p>
                    <div className="news-card-meta">
                      <span className="news-card-read">{item.readTime}</span>
                      <span className="news-card-link">
                        Read More <span className="arr">→</span>
                      </span>
                    </div>
                  </div>
                </NewsCardLink>
              ))
          }
        </div>

        {/* <div className="sec-cta fade-in">
          <button className="btn-outline" onClick={() => loadNews()} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh News'}
          </button>
        </div> */}
      </div>
    </section>
  );
}